import { getParentPath, getParentPaths } from "utils/objectData";

export { getParentPaths } from "utils/objectData";

import { ListNode, Node, Tree } from "./types";

/**
 * Expand all ancestor/parent nodes leading to the given node if they are not already expanded.
 * Does not expand the target node itself.
 * @returns `tree`
 */
export function ensureExpandedTo<T>(tree: Tree<T>, path: string) {
    let newTree = tree;
    for (const parentPath of getParentPaths(path)) {
        const node = tree[parentPath];
        if (!node) {
            continue;
        }
        if (!node.expanded || node.groupCollapsed) {
            if (newTree === tree) {
                newTree = { ...tree };
            }
            newTree[parentPath] = { ...node, expanded: true, groupCollapsed: false };
        }
    }

    const leafNode = newTree[path];
    if (leafNode && leafNode.groupCollapsed) {
        if (newTree === tree) {
            newTree = { ...tree };
        }
        newTree[path] = { ...leafNode, groupCollapsed: false };
    }

    return newTree;
}

/**
 * Expands group based on `groupKey`.
 * @returns `tree`
 */
export function expandGroup<T>(tree: Tree<T>, path: string, max?: number) {
    const node = tree[path];
    if (!node || !node.groupCollapsed) {
        return tree;
    }

    const parentPath = getParentPath(node.path);
    const parent = tree[parentPath];
    if (!parent || !parent.children) {
        return tree;
    }

    const nodeIndex = parent.children.indexOf(path);
    if (nodeIndex === -1) {
        return tree;
    }

    const newTree = { ...tree };

    const endIndex = max ? Math.min(parent.children.length, nodeIndex + max) : parent.children.length;
    for (let i = nodeIndex; i < endIndex; i++) {
        const childPath = parent.children[i];
        const child = newTree[childPath];
        if (child.groupKey !== node.groupKey || !child.groupCollapsed) {
            break;
        }

        newTree[childPath] = { ...child, groupCollapsed: false };
    }

    return newTree;
}

/**
 * Walks backward from `topVisibleItemIndex` to find the item that should be
 * pinned as the sticky header. Returns null when nothing should be pinned.
 *
 * Level-0 nodes are always candidates (pinned once scrolled off screen).
 * Additional candidates can be supplied via `isStickyHeaderCandidate` (e.g.
 * expanded container file nodes). A candidate is only pinned once it has fully
 * scrolled off the top (i < topIndex), so the sticky appears only after the
 * user has scrolled past the node itself — not the moment it hits the top edge.
 * This also means the previous file node's sticky lingers while the next sibling
 * file is still fully visible, which matches the designed behaviour.
 */
export function findStickyItem<T>(
    list: ListNode<T>[],
    topVisibleItemIndex: number,
    isStickyHeaderCandidate?: (node: Node<T>) => boolean,
): ListNode<T> | null {
    const topIndex = Math.max(0, Math.min(topVisibleItemIndex, list.length) - 1);
    for (let i = topIndex; i >= 0; i--) {
        const item = list[i];
        if (item?.level === 0) {
            return i < topIndex ? item : null;
        }
        if (item && isStickyHeaderCandidate?.(item.node)) {
            if (i < topIndex) {
                return item;
            }
        }
    }
    return null;
}

export function defaultBreadcrumbNodeLabel<T>(node: Node<T>) {
    if (node.name.length < 31) {
        return node.name;
    }
    return node.name.slice(0, 7) + "..." + node.name.slice(-7);
}

export function collapseAllNodes<T>(tree: Tree<T>) {
    const newTree = { ...tree };
    for (const path in newTree) {
        if (path !== "" && newTree[path].expanded) {
            newTree[path] = { ...newTree[path], expanded: false };
        }
    }
    return newTree;
}

/**
 * Convert tree into a flat list of indented items.
 * In addition to flattening it
 * - Collapses sequential nodes with the same `groupKey` (the "clickable small number" rows)
 *   unless `grouping` is false, in which case all nodes are emitted individually.
 *
 * Note: there is no synthetic "load more" row — remaining children are loaded automatically
 * in the background instead.
 */
export function makeNodeList<T>({ tree, grouping = true }: { tree: Tree<T>; grouping?: boolean }) {
    const result: ListNode<T>[] = [];
    type Frame = {
        node: Node<T>;
        level: number;
        listNode: ListNode<T> | null;
        childIndex: number;
        groupListNode?: ListNode<T>;
        nodeGroup: Node<T>[];
        prevChild?: Node<T>;
        lastListNode?: ListNode<T>;
        pendingChild?: Node<T>;
    };

    const createFrame = (node: Node<T>, level: number, listNode: ListNode<T> | null): Frame => ({
        node,
        level,
        listNode,
        childIndex: 0,
        nodeGroup: [],
    });
    const root = tree[""];
    if (!root) {
        return result;
    }

    // Use an explicit stack rather than recursion: imported models can contain hierarchy paths
    // deeper than the JavaScript call-stack limit.
    const stack: Frame[] = [createFrame(root, -1, null)];
    while (stack.length) {
        const frame = stack.at(-1)!;
        if (!frame.node.children?.length || !frame.node.expanded) {
            stack.pop();
            continue;
        }

        const finalizeGroup = () => {
            if (frame.groupListNode && frame.prevChild && frame.groupListNode.node !== frame.prevChild) {
                frame.groupListNode.nodeGroup = frame.nodeGroup;
            }
            frame.groupListNode = undefined;
        };

        // A child subtree has just been visited. Resume its parent exactly where the recursive
        // implementation would, including group bookkeeping after child traversal.
        if (frame.pendingChild) {
            const child = frame.pendingChild;
            frame.pendingChild = undefined;
            finalizeGroup();
            if (grouping && child.groupKey && child.groupCollapsed) {
                frame.groupListNode = frame.lastListNode;
                frame.nodeGroup = [child];
            }
            frame.prevChild = child;
            continue;
        }

        if (frame.childIndex < frame.node.children.length) {
            const childPath = frame.node.children[frame.childIndex++];
            const child = tree[childPath];

            if (
                grouping &&
                frame.groupListNode &&
                child.groupKey === frame.groupListNode.node.groupKey &&
                child.groupCollapsed
            ) {
                frame.prevChild = child;
                frame.nodeGroup.push(child);
                continue;
            }

            const listNode: ListNode<T> = {
                node: child,
                parent: frame.node,
                level: frame.level + 1,
                index: result.length,
            };
            frame.lastListNode = listNode;
            frame.pendingChild = child;
            result.push(listNode);
            stack.push(createFrame(child, frame.level + 1, listNode));
            continue;
        }

        finalizeGroup();
        // No synthetic "load more" row: remaining children are loaded automatically in the
        // background (see modelTree auto-load), matching the recursive implementation on develop.
        if (frame.lastListNode) {
            frame.lastListNode.isLast = true;
        }
        stack.pop();
    }

    return result;
}
